import React, { useEffect, useRef, useState } from "react";
import {
	Box,
	Button,
	Container,
	TextField,
	Typography,
	CircularProgress,
	Divider,
	InputAdornment,
	IconButton
} from "@mui/material";
import { AuthContainer, AuthWrapper } from "../styles";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { signupSchema } from "../schemas";
import { useLazyLoginWithGoogleQuery, useSignUpMutation } from "../../../store/services/auth/auth.service";
import { omit } from "lodash";
import { IError } from "../../../utils/getErrorMessage";
import { notifyError } from "../../../utils/NotifyError";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ReCAPTCHA from "react-google-recaptcha";
import RecaptchaDisclaimer from "../../../Components/RecaptchaDisclaimer";
import { ISignUp } from "../../../store/services/auth/dto/auth.dto";
import ButtonComponent from "../../../Components/ButtonComponent";
const Signup = () => {
	const [showPassword, setShowPassword] = useState(false);
	const navigate = useNavigate();
	const [callSignup, { isLoading }] = useSignUpMutation();
	const [isLoadingSingup, setIsLoadingSingup] = useState(false);
	const [callLoginWithGoogle] = useLazyLoginWithGoogleQuery();
	const recaptchaRef = useRef<ReCAPTCHA>(null);

	useEffect(() => {
		return () => {
			if (recaptchaRef.current) recaptchaRef.current.reset();
		};
	}, []);

	const handleOnSubmit = async (values: ISignUp) => {
		if (!recaptchaRef.current) {
			return notifyError({ error: "Recaptcha failed" } as IError);
		}
		setIsLoadingSingup(true);
		let recaptchaToken: string | null = "";
		try {
			recaptchaToken = await recaptchaRef.current.executeAsync();
		} catch (e) {
			console.error(e);
		}

		const _values = omit(values, ["confirmPassword"]);
		callSignup({ ..._values, email: values.email.toLowerCase(), recaptchaToken }).then(({ data, error }) => {
			if (data) {
				navigate("/");
			}
			const _error: { data: { message: string }; error?: string } = error as {
				data: { message: string };
				error?: string;
			};
			if (_error) {
				notifyError(error as IError);
			}
			setIsLoadingSingup(false);
		});
	};

	const {
		handleSubmit,
		handleChange,
		errors,
		values: { firstName, lastName, email, password, confirmPassword },
		dirty,
		isValid
	} = useFormik({
		validationSchema: signupSchema,
		initialValues: {
			firstName: "",
			lastName: "",
			email: "",
			password: "",
			confirmPassword: ""
		},
		onSubmit: handleOnSubmit
	});

	const responseMessage = (response: CredentialResponse) => {
		callLoginWithGoogle({ token: response.credential as string }).then(({ data, error }) => {
			if (data) {
				navigate("/");
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};

	const handleError = () => {
		console.log("error");
	};
	const handleClickShowPassword = () => setShowPassword(show => !show);

	return (
		<Container>
			<AuthContainer>
				<form onSubmit={handleSubmit}>
					<AuthWrapper>
						<Box className={"left_block"}>
							<Typography variant={"h6"} className={"title"}>
								Sign Up
							</Typography>
							<Box mt={2} width={"100%"}>
								<Box mb={2} display={"flex"} justifyContent={"space-between"}>
									<Box mr={1} className={"input_wrapper"} flexGrow={1}>
										<Typography variant={"subtitle1"} fontWeight={"bold"}>
											First Name
										</Typography>
										<TextField
											fullWidth
											type={"text"}
											value={firstName}
											name={"firstName"}
											onChange={handleChange}
											error={!!errors.firstName}
											helperText={errors.firstName}
											autoFocus={true}
										/>
									</Box>
									<Box className={"input_wrapper"} flexGrow={1}>
										<Typography variant={"subtitle1"} fontWeight={"bold"}>
											Last Name
										</Typography>
										<TextField
											fullWidth
											type={"text"}
											value={lastName}
											name={"lastName"}
											onChange={handleChange}
											error={!!errors.lastName}
											helperText={errors.lastName}
										/>
									</Box>
								</Box>
								<Box mb={2} className={"input_wrapper"}>
									<Typography variant={"subtitle1"} fontWeight={"bold"}>
										Email
									</Typography>
									<TextField
										fullWidth
										type={"email"}
										value={email}
										name={"email"}
										onChange={handleChange}
										error={!!errors.email}
										helperText={errors.email}
										autoComplete={"email"}
									/>
								</Box>
								<Box mb={2} className={"input_wrapper"}>
									<Typography variant={"subtitle1"} fontWeight={"bold"}>
										Password
									</Typography>
									<TextField
										fullWidth
										type={showPassword ? "text" : "password"}
										value={password}
										name={"password"}
										onChange={handleChange}
										error={!!errors.password}
										helperText={errors.password}
										slotProps={{
											input: {
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															aria-label={showPassword ? "hide password" : "display password"}
															onClick={handleClickShowPassword}
															edge="end"
														>
															{showPassword ? <VisibilityOff /> : <Visibility />}
														</IconButton>
													</InputAdornment>
												)
											}
										}}
									/>
								</Box>
								<Box mb={2} className={"input_wrapper"}>
									<Typography variant={"subtitle1"} fontWeight={"bold"}>
										Confirm Password
									</Typography>
									<TextField
										fullWidth
										type={showPassword ? "text" : "password"}
										value={confirmPassword}
										name={"confirmPassword"}
										onChange={handleChange}
										error={!!errors.confirmPassword}
										helperText={errors.confirmPassword}
										slotProps={{
											input: {
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															aria-label={showPassword ? "hide password" : "display password"}
															onClick={handleClickShowPassword}
															edge="end"
														>
															{showPassword ? <VisibilityOff /> : <Visibility />}
														</IconButton>
													</InputAdornment>
												)
											}
										}}
									/>
								</Box>
							</Box>
							<Box width={"100%"}>
								<ButtonComponent
									fullWidth
									variant={"contained"}
									type={"submit"}
									disabled={(dirty && !isValid) || isLoading || isLoadingSingup}
									text={"Sign Up"}
									loading={isLoading || isLoadingSingup}
								/>
							</Box>
							<RecaptchaDisclaimer />
							<Box width={"100%"} my={2}>
								<Divider />
							</Box>

							<Box
								display={"flex"}
								justifyContent={"center"}
								width={"100%"}
								py={1}
								flexDirection={"column"}
								alignItems={"center"}
								gap={3}
							>
								<Typography variant={"subtitle1"}>or Sign Up with</Typography>
								<GoogleLogin onSuccess={responseMessage} onError={handleError} />
							</Box>
						</Box>
						<Box className={"right_block"}>
							<Typography variant={"h4"}>Welcome to Sign Up</Typography>
							<Typography variant={"subtitle2"}>Already a user?</Typography>
							<Button variant={"contained"} size={"large"} onClick={() => navigate("/login")}>
								Login
							</Button>
						</Box>
					</AuthWrapper>
				</form>
			</AuthContainer>
			<ReCAPTCHA
				sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
				size="invisible"
				ref={recaptchaRef}
				badge="bottomright"
			/>
		</Container>
	);
};

export default Signup;
